import { useState, useEffect, useCallback, useMemo } from 'react';
import { supabase } from '../../lib/supabaseClient';

const WEEK_LIMIT = 6;   // last 6 weekly columns (per product spec)
const MONTH_LIMIT = 14; // up to same month last year (matches manual report span)

const MONTHS_ES = ['ENE', 'FEB', 'MAR', 'ABR', 'MAY', 'JUN', 'JUL', 'AGO', 'SEP', 'OCT', 'NOV', 'DIC'];
const DIA_NO_HABIL_ID = 19; // N/A activity carved out of "Horas internas"

/* ── Helpers ──────────────────────────────────────── */
function round2(n) {
  return Math.round((Number(n) || 0) * 100) / 100;
}

/** Compact number: 480 → "480", 397.5 → "397.5" */
function fmtNum(n) {
  return String(round2(n));
}

function monthAbbrev(dateStr) {
  if (!dateStr) return '';
  const m = parseInt(dateStr.slice(5, 7), 10) - 1;
  return MONTHS_ES[m] || '';
}

function yearOf(dateStr) {
  return dateStr ? dateStr.slice(0, 4) : '';
}

function formatDayDM(dateStr) {
  if (!dateStr) return '';
  const [, m, d] = dateStr.split('-');
  return `${d}/${m}`;
}

function htHours(s, id) {
  return Number(s?.hour_type_hours?.[id] || 0);
}
function actHours(s, id) {
  return Number(s?.activity_hours?.[id] || 0);
}
function internalHours(s) {
  // N/A total minus Día no hábil (shown on its own line)
  return htHours(s, '3') - actHours(s, String(DIA_NO_HABIL_ID));
}

/* ── Icons ────────────────────────────────────────── */
function IconDownload() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="7 10 12 15 17 10" />
      <line x1="12" y1="15" x2="12" y2="3" />
    </svg>
  );
}

function IconGenerate() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
      <line x1="16" y1="2" x2="16" y2="6" />
      <line x1="8" y1="2" x2="8" y2="6" />
      <line x1="3" y1="10" x2="21" y2="10" />
      <line x1="12" y1="14" x2="12" y2="18" />
      <line x1="10" y1="16" x2="14" y2="16" />
    </svg>
  );
}

/* ── Generate modal ───────────────────────────────── */
function GenerateModal({ open, onClose, onGenerated }) {
  const [loading, setLoading] = useState(true);
  const [preview, setPreview] = useState(null);
  const [loadError, setLoadError] = useState('');

  const [resourceCount, setResourceCount] = useState('');
  const [activeClients, setActiveClients] = useState('');
  const [externalDevs, setExternalDevs] = useState('');
  const [note, setNote] = useState('');

  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    setLoadError('');
    setSubmitError('');
    (async () => {
      const { data, error } = await supabase.rpc('preview_weekly_report_defaults');
      if (cancelled) return;
      if (error || !data || !data.length) {
        setLoadError('No se pudieron cargar los datos de la semana. Intenta de nuevo.');
        setLoading(false);
        return;
      }
      const p = data[0];
      setPreview(p);
      setResourceCount(String(p.resource_count ?? 0));
      setActiveClients(String(p.active_clients ?? 0));
      setExternalDevs(String(p.default_external_devs ?? 0));
      setNote(p.existing_note ?? '');
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [open]);

  // Esc to close
  useEffect(() => {
    if (!open) return;
    function handleKey(e) { if (e.key === 'Escape' && !submitting) onClose(); }
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [open, onClose, submitting]);

  if (!open) return null;

  const clampInt = (v) => {
    const n = parseInt(v, 10);
    return Number.isFinite(n) && n >= 0 ? n : 0;
  };

  async function handleSubmit(e) {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    setSubmitError('');
    const { data, error } = await supabase.rpc('generate_weekly_report_snapshot', {
      p_external_devs: clampInt(externalDevs),
      p_resource_count: clampInt(resourceCount),
      p_active_clients: clampInt(activeClients),
      p_note: note.trim() || null,
    });
    setSubmitting(false);
    if (error) {
      setSubmitError(
        error.message?.includes('No autorizado')
          ? 'No tienes permiso para generar este reporte.'
          : 'No se pudo generar el reporte. Intenta de nuevo.'
      );
      return;
    }
    onGenerated(data);
  }

  return (
    <div
      className="rep-modal-backdrop"
      onClick={(e) => { if (e.target === e.currentTarget && !submitting) onClose(); }}
    >
      <div className="rep-modal" role="dialog" aria-modal="true" aria-labelledby="rep-gen-title">
        <div className="rep-modal-header">
          <h3 id="rep-gen-title" className="rep-modal-title">Generar Reporte Semanal</h3>
          <button className="rep-modal-close" onClick={onClose} disabled={submitting} aria-label="Cerrar">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
          </button>
        </div>

        {loading ? (
          <div className="rep-modal-body">
            <div className="cat-loading">Cargando datos de la semana…</div>
          </div>
        ) : loadError ? (
          <div className="rep-modal-body">
            <div className="cat-error-banner">{loadError}</div>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <div className="rep-modal-body">
              <div className="rep-week-banner">
                <span className="rep-week-banner-tag">Semana objetivo</span>
                <span className="rep-week-banner-range">
                  {formatDayDM(preview.week_start)} – {formatDayDM(preview.week_end)} · {yearOf(preview.week_start)}
                </span>
              </div>

              <p className="rep-modal-context">
                {preview.log_count > 0
                  ? `Se detectaron ${fmtNum(preview.registered_hours)} h en ${preview.log_count} registro(s) de recursos internos esta semana.`
                  : 'No hay actividades de recursos internos registradas en esta semana. El reporte se generará con ceros en las horas.'}
              </p>

              {preview.already_exists && (
                <div className="rep-warn-banner">
                  Ya existe un reporte para esta semana. Al generarlo de nuevo se sobrescribirá.
                </div>
              )}

              <div className="rep-form-grid">
                <div className="act-filter-group">
                  <label className="act-filter-label" htmlFor="rep-res">Recursos de Fábrica</label>
                  <input
                    id="rep-res" type="number" min="0" step="1" className="cat-input"
                    value={resourceCount}
                    onChange={(e) => setResourceCount(e.target.value)}
                  />
                  <span className="rep-field-hint">Desarrolladores internos activos (autocalculado, editable).</span>
                </div>
                <div className="act-filter-group">
                  <label className="act-filter-label" htmlFor="rep-cli">Clientes activos</label>
                  <input
                    id="rep-cli" type="number" min="0" step="1" className="cat-input"
                    value={activeClients}
                    onChange={(e) => setActiveClients(e.target.value)}
                  />
                  <span className="rep-field-hint">Clientes con horas de recursos internos esta semana.</span>
                </div>
                <div className="act-filter-group">
                  <label className="act-filter-label" htmlFor="rep-ext">Desarrolladores externos ABAP</label>
                  <input
                    id="rep-ext" type="number" min="0" step="1" className="cat-input"
                    value={externalDevs}
                    onChange={(e) => setExternalDevs(e.target.value)}
                  />
                  <span className="rep-field-hint">Desarrolladores externos activos (autocalculado, editable).</span>
                </div>
              </div>

              <div className="act-filter-group rep-note-group">
                <label className="act-filter-label" htmlFor="rep-note">Nota (opcional)</label>
                <textarea
                  id="rep-note" className="cat-input rep-note-input" rows={2} maxLength={500}
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="Comentario para esta semana…"
                />
              </div>

              {submitError && <div className="cat-error-banner">{submitError}</div>}
            </div>

            <div className="rep-modal-footer">
              <button type="button" className="rep-clear-btn" onClick={onClose} disabled={submitting}>
                Cancelar
              </button>
              <button type="submit" className="rep-export-btn" disabled={submitting}>
                {submitting ? <span className="cat-spinner" /> : <IconGenerate />}
                <span>{submitting ? 'Generando…' : (preview.already_exists ? 'Regenerar' : 'Generar')}</span>
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

/* ── Component ────────────────────────────────────── */
function WeeklyReport({ onBack }) {
  const [weeks, setWeeks] = useState([]);
  const [months, setMonths] = useState([]);
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState('');
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setFetchError('');
    const [weeksRes, monthsRes, actsRes] = await Promise.all([
      supabase.from('report_period_snapshots').select('*')
        .eq('period_type', 'week').order('period_start', { ascending: false }).limit(WEEK_LIMIT),
      supabase.from('report_period_snapshots').select('*')
        .eq('period_type', 'month').order('period_start', { ascending: false }).limit(MONTH_LIMIT),
      supabase.from('activities').select('id, name, hour_type_id').order('hour_type_id').order('id'),
    ]);
    if (weeksRes.error || monthsRes.error || actsRes.error) {
      setFetchError('No se pudieron cargar los datos del reporte. Intenta de nuevo.');
      setLoading(false);
      return;
    }
    // stored newest-first; display oldest → newest (left → right)
    setWeeks([...(weeksRes.data || [])].reverse());
    setMonths([...(monthsRes.data || [])].reverse());
    setActivities(actsRes.data || []);
    setLoading(false);
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // auto-dismiss success message
  useEffect(() => {
    if (!successMsg) return;
    const t = setTimeout(() => setSuccessMsg(''), 5000);
    return () => clearTimeout(t);
  }, [successMsg]);

  /* Activity groupings (stable rows, ordered by catalog) */
  const groups = useMemo(() => ({
    facturables: activities.filter((a) => a.hour_type_id === 1),
    noFacturables: activities.filter((a) => a.hour_type_id === 2),
    internas: activities.filter((a) => a.hour_type_id === 3 && a.id !== DIA_NO_HABIL_ID),
  }), [activities]);

  /* Row blueprint — drives both on-screen table and Excel export */
  const rows = useMemo(() => {
    const r = [];
    r.push({ kind: 'count', label: 'Recursos de Fábrica', get: (s) => s.resource_count });
    r.push({ kind: 'count', label: 'Clientes activos', get: (s) => s.active_clients_count });
    r.push({ kind: 'count', label: 'Desarrolladores externos ABAP', get: (s) => s.external_devs_count });
    r.push({ kind: 'spacer' });
    r.push({ kind: 'hours', label: 'Horas cargables por semana / mes', get: (s) => s.chargeable_hours });
    r.push({ kind: 'hours', label: 'Total de horas registradas', get: (s) => s.registered_hours });
    r.push({ kind: 'util', label: '% de horas cargables registradas' });
    r.push({ kind: 'spacer' });
    r.push({ kind: 'cat', accent: 'fact', label: 'Horas a facturar', get: (s) => htHours(s, '1') });
    r.push({ kind: 'cat', accent: 'nofact', label: 'Horas no facturables', get: (s) => htHours(s, '2') });
    r.push({ kind: 'cat', accent: 'int', label: 'Horas internas', get: (s) => internalHours(s) });
    r.push({ kind: 'pct', label: 'Día no hábil', get: (s) => actHours(s, String(DIA_NO_HABIL_ID)) });
    r.push({ kind: 'spacer' });
    r.push({ kind: 'pct', label: 'Horas en horario extendido', get: (s) => s.extended_hours });
    r.push({ kind: 'spacer' });
    r.push({ kind: 'section', accent: 'fact', label: 'HORAS A FACTURAR', get: (s) => htHours(s, '1') });
    groups.facturables.forEach((a) => r.push({ kind: 'pct', indent: true, label: a.name, get: (s) => actHours(s, String(a.id)) }));
    r.push({ kind: 'spacer' });
    r.push({ kind: 'section', accent: 'nofact', label: 'HORAS NO FACTURABLES', get: (s) => htHours(s, '2') });
    groups.noFacturables.forEach((a) => r.push({ kind: 'pct', indent: true, label: a.name, get: (s) => actHours(s, String(a.id)) }));
    r.push({ kind: 'spacer' });
    r.push({ kind: 'section', accent: 'int', label: 'HORAS INTERNAS', get: (s) => internalHours(s) });
    groups.internas.forEach((a) => r.push({ kind: 'pct', indent: true, label: a.name, get: (s) => actHours(s, String(a.id)) }));
    return r;
  }, [groups]);

  /* Columns: weeks, then months (oldest → newest each) */
  const columns = useMemo(() => {
    const cols = [];
    weeks.forEach((s) => cols.push({
      key: `w-${s.id}`, kind: 'week', snapshot: s,
      top: monthAbbrev(s.period_start),
      bottom: s.iso_week != null ? `W${s.iso_week}` : '',
    }));
    months.forEach((s) => cols.push({
      key: `m-${s.id}`, kind: 'month', snapshot: s,
      top: yearOf(s.period_start),
      bottom: monthAbbrev(s.period_start),
    }));
    return cols;
  }, [weeks, months]);

  const hasData = weeks.length > 0 || months.length > 0;

  /* KPI cards for the most recent week */
  const latestWeek = weeks.length ? weeks[weeks.length - 1] : null;
  const latestUtil = latestWeek && latestWeek.chargeable_hours > 0
    ? Math.round((latestWeek.registered_hours / latestWeek.chargeable_hours) * 100)
    : null;

  /* Compute a cell value for both rendering and export */
  function computeCell(row, s) {
    if (row.kind === 'count') {
      const v = Math.round(row.get(s) || 0);
      return { text: String(v), num: v, fmt: '0', pct: false };
    }
    if (row.kind === 'hours') {
      const v = round2(row.get(s));
      return { text: fmtNum(v), num: v, fmt: '0.##', pct: false };
    }
    if (row.kind === 'util') {
      const base = Number(s.chargeable_hours) || 0;
      const reg = Number(s.registered_hours) || 0;
      if (base <= 0) return { text: '—', num: null, pct: true };
      const f = reg / base;
      return { text: `${Math.round(f * 100)}%`, num: f, fmt: '0%', pct: true };
    }
    // cat / section / pct → percentage of registered hours
    const reg = Number(s.registered_hours) || 0;
    if (reg <= 0) return { text: '—', num: null, pct: true };
    const f = (row.get(s) || 0) / reg;
    return { text: `${Math.round(f * 100)}%`, num: f, fmt: '0%', pct: true };
  }

  /* ── Excel export ── */
  async function exportToExcel() {
    if (!hasData || exporting) return;
    setExporting(true);
    setExportError('');
    try {
      const mod = await import('exceljs');
      const ExcelJS = mod.default ?? mod;

      const ROYAL = 'FF00115D';
      const CARIBBEAN = 'FF00CFC1';
      const PURPLAH = 'FF2E0055';
      const EERIE = 'FF222222';
      const WHITE = 'FFFFFFFF';
      const ZEBRA = 'FFF8F9FA';
      const thin = {
        top: { style: 'thin', color: { argb: 'FFD9DCE5' } },
        left: { style: 'thin', color: { argb: 'FFD9DCE5' } },
        bottom: { style: 'thin', color: { argb: 'FFD9DCE5' } },
        right: { style: 'thin', color: { argb: 'FFD9DCE5' } },
      };
      const accentFill = (accent) => {
        if (accent === 'fact') return CARIBBEAN;
        if (accent === 'nofact') return PURPLAH;
        if (accent === 'int') return ROYAL;
        return ROYAL;
      };
      const accentText = (accent) => (accent === 'fact' ? EERIE : WHITE);

      const wb = new ExcelJS.Workbook();
      wb.creator = 'T.R.A.C.K.';
      wb.created = new Date();
      const ws = wb.addWorksheet('Reporte Semanal', {
        views: [{ state: 'frozen', xSplit: 1, ySplit: 4 }],
      });

      // Blank separator column between the weekly and monthly blocks (visual gap only)
      const sepIndex = weeks.length > 0 && months.length > 0 ? weeks.length : -1;
      const excelCol = (i) => 2 + i + (sepIndex >= 0 && i >= sepIndex ? 1 : 0);

      const colCount = 1 + columns.length + (sepIndex >= 0 ? 1 : 0);
      ws.getColumn(1).width = 34;
      for (let i = 0; i < columns.length; i++) ws.getColumn(excelCol(i)).width = 9;
      if (sepIndex >= 0) ws.getColumn(2 + sepIndex).width = 3;

      /* Title */
      ws.mergeCells(1, 1, 1, colCount);
      const t = ws.getCell(1, 1);
      t.value = 'T.R.A.C.K. — Carga de Horas Fábrica ABAP (Reporte Semanal)';
      t.font = { bold: true, size: 15, color: { argb: WHITE } };
      t.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: ROYAL } };
      t.alignment = { vertical: 'middle', horizontal: 'center' };
      ws.getRow(1).height = 28;

      ws.mergeCells(2, 1, 2, colCount);
      const sub = ws.getCell(2, 1);
      sub.value = `Generado el ${new Date().toLocaleString('es-MX', { dateStyle: 'long', timeStyle: 'short' })}  ·  ${weeks.length} semana(s) · ${months.length} mes(es)`;
      sub.font = { italic: true, size: 9, color: { argb: 'FF666666' } };
      sub.alignment = { horizontal: 'center' };

      /* Two header rows (row 3 = top, row 4 = bottom) */
      const topRow = ws.getRow(3);
      const botRow = ws.getRow(4);
      topRow.getCell(1).value = '';
      botRow.getCell(1).value = '';
      columns.forEach((c, i) => {
        const ct = topRow.getCell(excelCol(i));
        ct.value = c.top;
        ct.font = { bold: true, size: 9, color: { argb: WHITE } };
        ct.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: c.kind === 'week' ? ROYAL : PURPLAH } };
        ct.alignment = { horizontal: 'center', vertical: 'middle' };
        ct.border = thin;
        const cb = botRow.getCell(excelCol(i));
        cb.value = c.bottom;
        cb.font = { bold: true, size: 10, color: { argb: WHITE } };
        cb.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: c.kind === 'week' ? ROYAL : PURPLAH } };
        cb.alignment = { horizontal: 'center', vertical: 'middle' };
        cb.border = thin;
      });
      for (const rr of [topRow, botRow]) {
        const lc = rr.getCell(1);
        lc.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: ROYAL } };
        lc.border = thin;
      }

      /* Data rows start at Excel row 5 */
      let xr = 5;
      rows.forEach((row) => {
        if (row.kind === 'spacer') { xr += 1; return; }
        const excelRow = ws.getRow(xr);
        const isHeavy = row.kind === 'section';
        const isCat = row.kind === 'cat';

        const labelCell = excelRow.getCell(1);
        labelCell.value = row.label;
        labelCell.border = thin;
        labelCell.alignment = { vertical: 'middle', horizontal: 'left', indent: row.indent ? 1 : 0 };
        if (isHeavy) {
          labelCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: accentFill(row.accent) } };
          labelCell.font = { bold: true, size: 10, color: { argb: accentText(row.accent) } };
        } else if (isCat) {
          labelCell.font = { bold: true, size: 10, color: { argb: ROYAL } };
        } else {
          labelCell.font = { size: 10, color: { argb: EERIE } };
        }

        columns.forEach((c, i) => {
          const cell = excelRow.getCell(excelCol(i));
          const v = computeCell(row, c.snapshot);
          if (v.num === null) {
            cell.value = '—';
          } else {
            cell.value = v.num;
            cell.numFmt = v.fmt;
          }
          cell.alignment = { horizontal: 'center', vertical: 'middle' };
          cell.border = thin;
          if (isHeavy) {
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: accentFill(row.accent) } };
            cell.font = { bold: true, size: 10, color: { argb: accentText(row.accent) } };
          } else if (isCat) {
            cell.font = { bold: true, size: 10, color: { argb: ROYAL } };
          } else {
            cell.font = { size: 10, color: { argb: EERIE } };
            if ((xr % 2) === 1) cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: ZEBRA } };
          }
        });
        xr += 1;
      });

      const buffer = await wb.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const stamp = new Date().toISOString().slice(0, 10);
      a.download = `Reporte_Semanal_${stamp}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      setExportError('No se pudo generar el archivo de Excel. Intenta de nuevo.');
    } finally {
      setExporting(false);
    }
  }

  function handleGenerated() {
    setModalOpen(false);
    setSuccessMsg('Reporte semanal generado correctamente.');
    fetchAll();
  }

  /* ── Render ── */
  return (
    <>
      <div className="act-report-view">
        <div className="cat-crud-header">
          <button type="button" className="cat-back-btn" onClick={onBack}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6" /></svg>
            Volver
          </button>
          <h1 className="cat-crud-title">Reporte Semanal</h1>
          <div className="rep-header-actions">
            <button type="button" className="rep-generate-btn" onClick={() => setModalOpen(true)}>
              <IconGenerate />
              <span>Generar Reporte Semanal</span>
            </button>
            <button
              type="button"
              className="rep-export-btn rep-export-btn--excel"
              onClick={exportToExcel}
              disabled={exporting || loading || !hasData}
              title={!hasData ? 'No hay datos para exportar' : 'Exportar a Excel'}
            >
              {exporting ? <span className="cat-spinner" /> : <IconDownload />}
              <span>{exporting ? 'Exportando…' : 'Exportar a Excel'}</span>
            </button>
          </div>
        </div>

        {/* KPI cards (latest week) */}
        {latestWeek && (
          <div className="rep-summary-row">
            <div className="act-summary-card">
              <span className="act-summary-label">Última semana</span>
              <span className="act-summary-value">{latestWeek.iso_week != null ? `W${latestWeek.iso_week}` : '—'}</span>
            </div>
            <div className="act-summary-card">
              <span className="act-summary-label">% Utilización</span>
              <span className="act-summary-value">{latestUtil != null ? `${latestUtil}%` : '—'}</span>
            </div>
            <div className="act-summary-card">
              <span className="act-summary-label">Horas registradas</span>
              <span className="act-summary-value">{fmtNum(latestWeek.registered_hours)}</span>
            </div>
            <div className="act-summary-card">
              <span className="act-summary-label">Recursos de Fábrica</span>
              <span className="act-summary-value">{latestWeek.resource_count}</span>
            </div>
          </div>
        )}

        {successMsg && <div className="rep-success-banner rep-banner">{successMsg}</div>}
        {fetchError && <div className="cat-error-banner rep-banner">{fetchError}</div>}
        {exportError && <div className="cat-error-banner rep-banner">{exportError}</div>}

        {loading ? (
          <div className="cat-loading">Cargando reporte…</div>
        ) : !hasData ? (
          <div className="cat-empty rep-empty">
            <p>Aún no se ha generado ningún reporte semanal.</p>
            <button type="button" className="rep-generate-btn" onClick={() => setModalOpen(true)}>
              <IconGenerate />
              <span>Generar el primero</span>
            </button>
          </div>
        ) : (
          <div className="rep-matrix-wrap">
            <table className="rep-matrix">
              <thead>
                <tr className="rep-matrix-grouprow">
                  <th className="rep-matrix-corner" rowSpan={2} />
                  {weeks.length > 0 && <th className="rep-matrix-group rep-matrix-group--wk" colSpan={weeks.length}>Últimas {weeks.length} semana(s)</th>}
                  {months.length > 0 && <th className="rep-matrix-group rep-matrix-group--mo" colSpan={months.length}>Meses ({months.length})</th>}
                </tr>
                <tr className="rep-matrix-headrow">
                  {columns.map((c) => (
                    <th key={c.key} className={`rep-matrix-colhead rep-matrix-colhead--${c.kind}`}>
                      <span className="rep-colhead-top">{c.top}</span>
                      <span className="rep-colhead-bottom">{c.bottom}</span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((row, ri) => {
                  if (row.kind === 'spacer') {
                    return <tr key={`sp-${ri}`} className="rep-matrix-spacer"><td colSpan={1 + columns.length} /></tr>;
                  }
                  const rowCls =
                    row.kind === 'section' ? `rep-matrix-section rep-accent--${row.accent}`
                      : row.kind === 'cat' ? `rep-matrix-cat rep-accent--${row.accent}`
                        : '';
                  return (
                    <tr key={`r-${ri}`} className={rowCls}>
                      <th className={`rep-matrix-rowlabel${row.indent ? ' rep-matrix-rowlabel--indent' : ''}`}>
                        {row.label}
                      </th>
                      {columns.map((c) => {
                        const v = computeCell(row, c.snapshot);
                        return <td key={c.key} className="rep-matrix-cell">{v.text}</td>;
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <GenerateModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onGenerated={handleGenerated}
      />
    </>
  );
}

export default WeeklyReport;
